import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/joy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatISTDate } from '../../utils/date';

interface AdminWeeklySadhanaCardViewProps {
  card: any; // mentor-style WeeklySadhanaCard payload
}

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getDayNameFromDate(dateStr?: string) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const idx = Number.isNaN(d.getTime()) ? -1 : d.getDay();
  return idx >= 0 ? dayNames[idx] : '';
}

export const AdminWeeklySadhanaCardView: React.FC<AdminWeeklySadhanaCardViewProps> = ({ card }) => {
  const weekDateLabel = card?.weekStartDate && card?.weekEndDate
    ? `${formatISTDate(card.weekStartDate)} - ${formatISTDate(card.weekEndDate)}`
    : 'Dates not available';

  const bestDayLabel = typeof card?.bestDay === 'number'
    ? dayNames[(card.bestDay + 1) % 7]
    : '—';

  return (
    <Stack spacing={3}>
      {/* HEADER & STATS */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography level="h3">Weekly Sadhana Card</Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {weekDateLabel}
                </Typography>
              </Box>
              {card?.isSubmitted && (
                <Chip variant="soft" color="success" startDecorator={<CheckCircleIcon />}>
                  Submitted
                </Chip>
              )}
            </Box>

            <Grid container spacing={2}>
              <Grid xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography level="h2" sx={{ color: 'success.main' }}>
                    {card?.dailyAveragePoints?.toFixed?.(1) ?? '—'}
                  </Typography>
                  <Typography level="body-sm">Avg Points/Day</Typography>
                </Box>
              </Grid>
              <Grid xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography level="h2" sx={{ color: 'warning.main' }}>
                    {card?.weeklyStreak ?? '—'}
                  </Typography>
                  <Typography level="body-sm">Week Streak</Typography>
                </Box>
              </Grid>
              <Grid xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography level="h2">{bestDayLabel}</Typography>
                  <Typography level="body-sm">Best Day</Typography>
                </Box>
              </Grid>
              <Grid xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography level="h2">{card?.entries?.length ?? 0}</Typography>
                  <Typography level="body-sm">Days Entered</Typography>
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      {/* DAILY ENTRIES */}
      {Array.isArray(card?.entries) && card.entries.length > 0 && (
        <Box>
          <Typography level="h4" sx={{ mb: 2 }}>
            Daily Entries
          </Typography>
          <Grid container spacing={2}>
            {card.entries.map((entry: any, idx: number) => {
              const dayLabel = entry?.date ? entry.date.split('T')[0] : `Day ${idx + 1}`;
              const dayName = entry?.dayName || getDayNameFromDate(entry?.date) || (typeof entry?.dayOfWeek === 'number' ? dayNames[entry.dayOfWeek] : null) || `Day ${idx + 1}`;
              const totalSeva = (entry?.seva1Minutes || 0) + (entry?.seva2Minutes || 0) + (entry?.seva3Minutes || 0) + (entry?.seva4Minutes || 0);
              const bodyTotal = (entry?.bodyToBed || 0) + (entry?.bodyEarlyMorning || 0) + (entry?.bodyDaySleep || 0) + (entry?.bodyMangalaArti || 0);
              const soulTotal = (entry?.soulJapaQuality || 0) + (entry?.soulBookReading || 0) + (entry?.soulLectureHearing || 0) + (entry?.soulWeeklyClass || 0);
              const dutyTotal = (entry?.dutyDeityWorship || 0) + (entry?.dutyAttendance || 0);
              const chantingPoints = entry?.chantingQualityRounds || 0;
              const totalPoints = entry?.totalPoints ?? entry?.points ?? entry?.marksAwarded ?? (bodyTotal + soulTotal + dutyTotal + chantingPoints);

              return (
                <Grid xs={12} sm={6} key={idx}>
                  <Card variant="outlined">
                    <CardContent>
                      <Stack spacing={1.25}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography level="h4">{dayName}</Typography>
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{dayLabel}</Typography>
                        </Box>

                        <Chip
                          size="md"
                          variant="soft"
                          color={totalPoints >= 200 ? 'success' : totalPoints >= 150 ? 'warning' : 'neutral'}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          {totalPoints}
                        </Chip>

                        <Grid container spacing={1}>
                          <Grid xs={6}>
                            <Typography level="body-xs">🎵 Chanting</Typography>
                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                              {entry?.chantingQualityRounds || 0}
                            </Typography>
                          </Grid>
                          <Grid xs={6}>
                            <Typography level="body-xs">🧘 Body</Typography>
                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                              {bodyTotal}/150
                            </Typography>
                          </Grid>
                          <Grid xs={6}>
                            <Typography level="body-xs">📚 Soul</Typography>
                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                              {soulTotal}/135
                            </Typography>
                          </Grid>
                          <Grid xs={6}>
                            <Typography level="body-xs">🙏 Duty</Typography>
                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                              {dutyTotal}/60
                            </Typography>
                          </Grid>
                          <Grid xs={6}>
                            <Typography level="body-xs">🤝 Seva</Typography>
                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                              {totalSeva}m
                            </Typography>
                          </Grid>
                        </Grid>

                        {entry?.dailyRemarks && (
                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                              {entry.dailyRemarks.length > 100
                                ? `${entry.dailyRemarks.slice(0, 100)}...`
                                : entry.dailyRemarks}
                            </Typography>
                          </Box>
                        )}

                        {[1, 2, 3, 4].map((sevaNum) => {
                          const desc = entry?.[`seva${sevaNum}Description`];
                          const mins = entry?.[`seva${sevaNum}Minutes`];
                          return (
                            desc && mins > 0 && (
                              <Box key={sevaNum} sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                  <strong>Seva {sevaNum}:</strong> {desc} ({mins}m)
                                </Typography>
                              </Box>
                            )
                          );
                        })}

                        {entry?.practices && (
                          <Stack spacing={0.5}>
                            {Object.entries(entry.practices).map(([key, value]: any) => (
                              <Typography key={key} level="body-xs">
                                {key}: <strong>{String(value)}</strong>
                              </Typography>
                            ))}
                          </Stack>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* MENTOR COMMENT */}
      {card?.mentorComment && (
        <Card variant="soft" color="primary">
          <CardContent>
            <Stack spacing={1}>
              <Typography level="h4" startDecorator="💬">
                Mentor Feedback
              </Typography>
              <Typography level="body-sm">{card.mentorComment}</Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                {card.mentorCommentedAt && formatISTDate(card.mentorCommentedAt)}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default AdminWeeklySadhanaCardView;
